import React, { useState } from 'react';
import Drawer from '../Common/Drawer/Drawer';
import Toggle from '../Common/Toggle/Toggle';
import { isAllLocationsMode } from '@Helpers/taskListHelpers';
import type { TaskColumn, LocationTask, TaskSearchRequest } from '../../apis/locationTasks/locationTask.types';

// The `filters` object assembled by TaskListPage (feature flags + column definitions)
interface ColumnFiltersSettings {
    masterColumns: TaskColumn[];
    savedColumns: TaskColumn[];
    isProjectWidgetActive: boolean;
    isStartDateActive: boolean;
    isTaskStepsActive: boolean;
    isTaskDependenciesActive: boolean;
}

interface ColumnToggleValue {
    id: number;
    displayName: string;
    fieldName: string;
    isSaved: boolean;
}

interface ColumnFiltersDrawerProps {
    onComplete: (columnIds: number[]) => void;
    onCancel: () => void;
    onReset: () => void;
    filters: ColumnFiltersSettings;
    searchParams?: TaskSearchRequest;
    // Accepted for parity with the previous component API, but not used here.
    locationTasks?: LocationTask[];
}

export const ColumnFiltersDrawer = ({ onComplete, onCancel, onReset, filters, searchParams }: ColumnFiltersDrawerProps) => {
    // Determines if a column should be shown based on special feature rules
    const isColumnFeatureEnabled = (fieldName: string): boolean => {
        switch (fieldName) {
            case 'projectName':
                return filters.isProjectWidgetActive;
            case 'startDate':
                return filters.isStartDateActive;
            case 'steps':
                return filters.isTaskStepsActive;
            case 'prereqs':
                return filters.isTaskDependenciesActive;
            case 'locationName':
                /**
                 * 1320: The list only renders the Location column when the results can
                 * span more than one location, so don't offer a toggle that can't take
                 * effect.  Keep this in sync with ListView's isColumnFeatureEnabled.
                 */
                return (
                    isAllLocationsMode(searchParams) ||
                    !!(searchParams?.locationKeys && searchParams.locationKeys.length > 1)
                );
            default:
                return true;
        }
    };

    // Merge master columns with the user's saved columns (mirrors the old componentDidMount)
    const [values, setValues] = useState<ColumnToggleValue[]>(() =>
        filters.masterColumns
            .filter((masterColumn) => isColumnFeatureEnabled(masterColumn.fieldName))
            .map((masterColumn) => {
                const savedColumn = filters.savedColumns.find((m) => m.fieldName === masterColumn.fieldName);

                // Toggle is checked when a saved record exists, or when the user has no saved
                // columns at all and the master column is a default.
                const isDefault = filters.savedColumns.length === 0 ? !!masterColumn.isDefault : false;
                const isSaved = savedColumn ? true : isDefault;

                return {
                    id: masterColumn.columnId,
                    displayName: masterColumn.displayName,
                    fieldName: masterColumn.fieldName,
                    isSaved,
                };
            })
    );

    const onSave = () => {
        const arrayOfColumnIds = values.filter((v) => v.isSaved).map((v) => v.id);
        onComplete(arrayOfColumnIds);
    };

    const onToggleChange = (name: string, value: boolean) => {
        setValues((prev) => prev.map((column) => (column.displayName === name ? { ...column, isSaved: value } : column)));
    };

    const additionalDrawerButtons = [
        <button key='reset' type='button' className='drawer-button btn btn-warning button--cancel' onClick={onReset}>
            Reset
        </button>,
    ];

    return (
        <Drawer
            isOpen={true}
            isWide={false}
            title='Columns'
            onSaveClick={onSave}
            saveText='Save'
            onCancelClick={onCancel}
            formButtons={additionalDrawerButtons}>
            {values.map((column) => (
                <div key={column.fieldName}>
                    <Toggle name={column.displayName} text={column.displayName} checked={column.isSaved} onChange={onToggleChange} />
                </div>
            ))}
        </Drawer>
    );
};

export default ColumnFiltersDrawer;
